import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BellRinging, CheckCircle, XCircle, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { getSubscriptionStatus, refreshSubscription } from "@/lib/onesignal";
import { toast } from "sonner";

/**
 * Diagnostic card shown to clients so they can self-validate (and self-fix)
 * push notification subscription issues — the #1 reason "I'm not getting alerts".
 */
const PushDiagnostic = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const s = await getSubscriptionStatus();
    setStatus(s);
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const reactivate = async () => {
    setBusy(true);
    try {
      const r = await refreshSubscription();
      if (r.ok) {
        toast.success("Notificações ativadas com sucesso!");
        await load();
      } else {
        toast.error("Não foi possível ativar. Verifique as permissões do app no celular.");
      }
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const isOk = status.available && status.player_id && (status.opted_in !== false);
  const needsAction = status.available && (!status.player_id || status.opted_in === false || status.notification_permission === "denied");

  return (
    <Card className={`border p-4 card-soft ${isOk ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid="push-diagnostic-card">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center flex-shrink-0 ${isOk ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {isOk ? <CheckCircle size={20} weight="fill"/> : <BellRinging size={20} weight="duotone"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold">
            {isOk ? "Notificações ativadas" : "Ative suas notificações"}
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            {isOk
              ? "Você receberá lembretes de vencimento, confirmações de pagamento e novidades."
              : status.notification_permission === "denied"
                ? "Notificações estão BLOQUEADAS nas configurações do celular. Ative em: Configurações → Apps → Zeferius Pay → Notificações."
                : !status.available
                  ? (status.reason || "Push indisponível neste dispositivo.")
                  : !status.player_id
                    ? "Toque em \"Ativar agora\" abaixo. Quando o celular pedir permissão, aceite."
                    : "Toque em \"Ativar agora\" para reativar."}
          </div>
          {status.player_id && (
            <div className="text-[10px] text-zinc-400 mt-1 tracking-wider font-mono truncate">
              Sub: {status.player_id.slice(0, 8)}...{status.player_id.slice(-6)}
              {status.external_id ? (
                <span className="ml-2 text-emerald-700">· External ID linkado ✓</span>
              ) : (
                <span className="ml-2 text-amber-700">· External ID PENDENTE</span>
              )}
            </div>
          )}
          {needsAction && (
            <Button
              onClick={reactivate}
              disabled={busy}
              size="sm"
              className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="push-reactivate-btn"
            >
              {busy ? <><ArrowClockwise className="animate-spin mr-2" size={14}/> Ativando...</> : <><BellRinging className="mr-2" size={14}/> Ativar agora</>}
            </Button>
          )}
          {!status.available && (
            <div className="mt-2 text-[11px] text-amber-800 bg-amber-100 rounded px-2 py-1 inline-flex items-start gap-1">
              <WarningCircle size={12} weight="fill" className="mt-0.5 flex-shrink-0"/>
              <span>Você está usando o app via navegador. Para notificações nativas, instale o APK.</span>
            </div>
          )}
        </div>
        {!needsAction && (
          <Button onClick={reactivate} disabled={busy} variant="ghost" size="icon" data-testid="push-refresh-btn" title="Atualizar status">
            <ArrowClockwise size={16} className={busy ? "animate-spin" : ""}/>
          </Button>
        )}
      </div>
    </Card>
  );
};

export default PushDiagnostic;
